import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateRelocationMutation } from "@/app/Relocations/GraphQL/relocationMutations";
import { useQueryClient } from "@tanstack/react-query";
import { useRelocationRecord } from "@/app/Relocations/Hooks/useRelocationRecord";
import { Currency, FeatureFlagType, HireUnitType } from "@/gql/graphql";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { useTranslations } from "use-intl";
import { useFormikContext } from "formik";
import { DollarSignIcon } from "lucide-react";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { relocationPricingValidation } from "@/app/Relocations/Validation/relocationValidation";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { useFeatureFlag } from "@/app/Meta/utils/useFeatureFlag";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";

type PricingFormValues = {
  hire_unit_rate: number;
  extra_hire_unit_rate: number;
  extra_hire_unit_deposit_rate: number;
  extra_hire_unit_supplier_net_rate: number | null;
  currency: Currency;
  hire_unit_type: HireUnitType;
};

function PricingFormFields() {
  const tForm = useTranslations("relocation.form");
  const t = useTranslations("relocation");
  const { isAdmin } = useTenant();
  const variableRelocationRateEnabled = useFeatureFlag(
    FeatureFlagType.VariableRelocationRate,
  );
  const { values, setFieldValue } = useFormikContext<PricingFormValues>();

  const capitalizedUnitLabel = t(
    `hire_unit_type_capitalized.${values.hire_unit_type ?? HireUnitType.Day}`,
  ) as string;

  return (
    <div className="col-span-full grid grid-cols-3 gap-4 sm:grid-cols-6">
      <CurrencyInput
        name="hire_unit_rate"
        label={tForm("relocationRate")}
        tooltip={tForm("relocationRateTooltip")}
        currency={values.currency}
        isEditable={isAdmin || variableRelocationRateEnabled}
        className="col-span-3"
      />
      <CurrencyInput
        currency={values.currency}
        isEditable={false}
        name="extra_hire_unit_rate"
        label={tForm("extraUnitRate", { unit: capitalizedUnitLabel })}
        className="col-span-3"
      />
      <CurrencyInput
        currency={values.currency}
        onChange={async (value) => {
          if (value === null) return;
          await setFieldValue(
            "extra_hire_unit_rate",
            value + values.extra_hire_unit_deposit_rate,
          );
        }}
        name="extra_hire_unit_supplier_net_rate"
        label={tForm("supplierNet")}
        className="col-span-3"
      />
      <CurrencyInput
        currency={values.currency}
        isEditable={isAdmin}
        name="extra_hire_unit_deposit_rate"
        onChange={(value) => {
          setFieldValue(
            "extra_hire_unit_rate",
            (value ?? 0) + (values.extra_hire_unit_supplier_net_rate ?? 0),
          );
        }}
        label={tForm("imoovaNet")}
        className="col-span-3"
      />
    </div>
  );
}

function PricingViewState({ record }: { record: PricingFormValues }) {
  const tForm = useTranslations("relocation.form");
  const t = useTranslations("relocation");

  const capitalizedUnitLabel = t(
    `hire_unit_type_capitalized.${record.hire_unit_type ?? HireUnitType.Day}`,
  ) as string;

  return (
    <dl className="col-span-full grid grid-cols-2 gap-4 text-sm">
      <div>
        <dt className="text-gray-500">{tForm("relocationRate")}</dt>
        <dd className="font-medium">
          {formatCurrency(record.hire_unit_rate, record.currency, {
            showZero: true,
          })}
        </dd>
      </div>
      <div>
        <dt className="text-gray-500">
          {tForm("extraUnitRate", { unit: capitalizedUnitLabel })}
        </dt>
        <dd className="font-medium">
          {formatCurrency(record.extra_hire_unit_rate, record.currency)}
        </dd>
      </div>
      <div>
        <dt className="text-gray-500">{tForm("supplierNet")}</dt>
        <dd className="font-medium">
          {formatCurrency(
            record.extra_hire_unit_supplier_net_rate ?? 0,
            record.currency,
          )}
        </dd>
      </div>
      <div>
        <dt className="text-gray-500">{tForm("imoovaNet")}</dt>
        <dd className="font-medium">
          {formatCurrency(record.extra_hire_unit_deposit_rate, record.currency)}
        </dd>
      </div>
    </dl>
  );
}

export function PricingFormCard() {
  const queryClient = useQueryClient();
  const { mutateAsync } = useGqlMutation(updateRelocationMutation, {
    onSuccess: () => queryClient.invalidateQueries(),
  });
  const record = useRelocationRecord();
  const tForm = useTranslations("relocation.form");

  const initialValues: PricingFormValues = {
    hire_unit_rate: record.hire_unit_rate,
    extra_hire_unit_rate: record.extra_hire_unit_rate ?? 0,
    extra_hire_unit_deposit_rate: record.extra_hire_unit_deposit_rate ?? 0,
    extra_hire_unit_supplier_net_rate:
      record.extra_hire_unit_supplier_net_rate ?? null,
    currency: record.currency,
    hire_unit_type: record.hire_unit_type,
  };

  return (
    <RecordScreenFormCard<PricingFormValues>
      containerClassName="rounded-lg border border-gray-200 bg-white p-6"
      title={
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-amber-100 p-2">
            <DollarSignIcon className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <div>{tForm("pricingTitle")}</div>
            <p className="text-sm font-normal text-gray-500">
              {tForm("pricingDescription")}
            </p>
          </div>
        </div>
      }
      initialValues={initialValues}
      validationSchema={toFormikValidationSchema(relocationPricingValidation)}
      view={<PricingViewState record={initialValues} />}
      onSubmit={async (values) => {
        await mutateAsync({
          id: record.id,
          input: {
            hire_unit_rate: values.hire_unit_rate,
            extra_hire_unit_rate: values.extra_hire_unit_rate,
            extra_hire_unit_deposit_rate: values.extra_hire_unit_deposit_rate,
          },
        });
      }}
    >
      <PricingFormFields />
    </RecordScreenFormCard>
  );
}
